import GoNextBlockButton from "components/task/content/block/GoNextBlockButton";
import { Box, Sheet } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import {
  getAiCommentKeyInBlock,
  getTaskIdFromTaskKey,
  isBlockUserFieldFilled,
  isLineHidden,
} from "logic/logics";
import CoachMenuBar from "./CoachMenuBar";
import { layoutAtom } from "recoil/layoutAtom";
import { ProgramContentType } from "data/BlockComponent";
import React, { memo, useEffect, useRef } from "react";
import DoneButton from "components/task/change-status-button/DoneButton";
import Line from "./Line";
import { deviceAtom } from "recoil/deviceAtom";
import { CommentArrowSvg } from "static/image/common/SvgAssets";
import useDetectIOS from "../../../../hooks/useDetectIOS";
import type { TaskContentViewerMode } from "../TaskContent";

interface PropsType {
  taskKey: string;
  thoughtRecordKey?: string;
  activityRecordKey?: string;
  meditationRecordKey?: string;
  data: ProgramContentType[];
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  blockDataStr: string;
  index: number;
  complete: (index: number) => void;
  isCurrentIndex: boolean;
  doneWithEnter?: boolean;
  setDoneWithEnter?: React.Dispatch<React.SetStateAction<boolean>>;
  isDone?: boolean;
  isOpen?: boolean;
  isLastIndex: boolean;
  isBeforeNoBorder?: boolean;
  isNextOpen?: boolean;
  translationVersion?: string;
  viewerMode?: TaskContentViewerMode;
  setMoveToIndex?: React.Dispatch<React.SetStateAction<number | undefined>>;
  onUndoneSuccess?: () => void;
  onAttemptEdit?: () => void;
}

const Block = React.forwardRef<HTMLDivElement, PropsType>(
  (
    {
      taskKey,
      thoughtRecordKey,
      activityRecordKey,
      meditationRecordKey,
      data,
      setData,
      blockDataStr,
      index,
      complete,
      isCurrentIndex,
      doneWithEnter,
      setDoneWithEnter,
      isDone,
      isOpen,
      isLastIndex,
      isBeforeNoBorder,
      isNextOpen,
      translationVersion,
      viewerMode = "auto",
      setMoveToIndex,
      onUndoneSuccess,
      onAttemptEdit,
    }: PropsType,
    ref
  ) => {
    const blockData: ProgramContentType = {
      ...JSON.parse(blockDataStr),
    };

    const divRef = useRef<HTMLDivElement | null>(null);

    const user = useRecoilValue(userAtom);
    const device = useRecoilValue(deviceAtom);
    const isCoach =
      viewerMode === "auto"
        ? (!!user?.accessToken && user?.role !== "patient") || false
        : viewerMode === "coach";
    const { isIOS } = useDetectIOS();

    const isThoughtRecord = taskKey.includes("2-2-T");
    const isReadOnlyTask = taskKey.includes("0-3-W");

    const { taskWidth } = useRecoilValue(layoutAtom);

    const highlight = blockData && blockData.isHighlight;

    const isHidden =
      blockData &&
      (blockData.isHidden ||
        blockData.isAlwaysHidden ||
        (isCoach && !blockData.isShown && blockData.hideIfCoach) ||
        (!isCoach && (!blockData.isShown || blockData.hideIfPatient)));

    const focusElement = (el: HTMLElement) => {
      el.focus();
      try {
        const input = el as HTMLInputElement | HTMLTextAreaElement;
        const len = input.value?.length || 0;
        input.setSelectionRange?.(len, len);
      } catch {}

      if (isIOS) {
        try {
          el.click();
        } catch {}
      }
    };

    useEffect(() => {
      if (isCurrentIndex && !isCoach && divRef.current) {
        const focusables = Array.from(
          divRef.current.querySelectorAll<HTMLElement>(
            'textarea, input[type="text"], input[type="number"], input[type="tel"], .ql-editor'
          )
        );
        const inputEl = focusables.find(
          (el) =>
            !el.closest("[data-feedback-cell]") &&
            !el.closest("[data-disable-autofocus]")
        );
        if (inputEl) {
          focusElement(inputEl);
        }
      }
    }, [isCurrentIndex, isCoach]);

    return blockData ? (
      <div
        ref={(el) => {
          divRef.current = el;
          if (typeof ref === "function") {
            ref(el);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }
        }}
        key={`${taskKey}_${index}`}
        style={{
          width: "100%",
          margin: "0 auto",
          marginBottom: "8px",
          ...(taskWidth === "normal" && { maxWidth: "660px" }),
          ...(isHidden && { display: "none" }),
          ...(!blockData.isShown && { opacity: 0.5 }),
        }}
      >
        <div
          className={`${
            blockData.indentation && "flex items-start gap-[10px]"
          }`}
        >
          {blockData.indentation && <CommentArrowSvg />}

          <Sheet
            sx={{
              position: "relative",
              width: "100%",
              ...(!blockData.noBorder
                ? {
                    backgroundColor: "white",
                    borderRadius: "12px",
                    px: "20px",
                    py: "16px",
                  }
                : {
                    backgroundColor: "transparent",
                    py: "12px",
                    pl: "10px",
                    ...(index > 0 && { pt: "24px" }),
                  }),
            }}
          >
            {highlight && (
              <Box
                sx={{
                  backgroundColor: "primary.solid",
                  width: "5px",
                  height: "calc(100% - 40px)",
                  borderRadius: "10px",
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  transform: "translate(0, -50%)",
                }}
              ></Box>
            )}

            {blockData.lines?.map(
              (eachLine, lineIndex) =>
                !isLineHidden(data, blockData, eachLine) && (
                  <Line
                    key={`${taskKey}_line_${lineIndex}`}
                    lineStr={JSON.stringify(eachLine)}
                    lineIndex={lineIndex}
                    taskKey={taskKey}
                    thoughtRecordKey={thoughtRecordKey}
                    meditationRecordKey={meditationRecordKey}
                    activityRecordKey={activityRecordKey}
                    data={
                      eachLine.some(
                        (element) =>
                          element.type === "typographyFromData" ||
                          element.type === "pieChart" ||
                          element.type === "percentage" ||
                          element.type === "extractFromThoughtRecord"
                      )
                        ? data
                        : undefined
                    }
                    setData={setData}
                    blockIndex={index}
                    isLastLine={
                      lineIndex === blockData.lines.length - 1 ||
                      // 생기지에서 textarea 아래에 help 있는 경우 margin-bottom 을 0으로 주기 위해
                      (isThoughtRecord &&
                        blockData.lines[lineIndex]?.[0]?.type === "textarea" &&
                        blockData.lines[lineIndex + 1]?.[0]?.type === "help")
                    }
                    translationVersion={translationVersion}
                    readonly={isReadOnlyTask || (!isCoach && ((isDone && !isLastIndex) || false))}
                    isShown={blockData.isShown || false}
                    viewerMode={viewerMode}
                    hasDivider={
                      lineIndex === 0 &&
                      blockData.lines[lineIndex].length === 1 &&
                      blockData.lines.length > 1 &&
                      [
                        "typography",
                        // "typographyFromData",
                        "thoughtmap",
                        "preset",
                        "reference",
                        "conceptItem",
                        "isolation",
                      ].includes(blockData.lines[lineIndex + 1][0].type)
                    }
                    onAttemptEdit={onAttemptEdit}
                  />
                )
            )}
            {!isCoach &&
              (isLastIndex || blockData.isEnd) &&
              !taskKey.includes("Tool") && (
                <Box sx={{ mt: "12px" }}>
                  <DoneButton
                    taskKey={taskKey}
                    data={data}
                    doneWithEnter={doneWithEnter}
                    setDoneWithEnter={setDoneWithEnter}
                    setMoveToIndex={setMoveToIndex}
                    thoughtRecordKey={thoughtRecordKey}
                    meditationRecordKey={meditationRecordKey}
                    isDone={isDone}
                    isOpen={isOpen}
                    translationVersion={translationVersion}
                  />
                </Box>
              )}
          </Sheet>
        </div>

        {!isLastIndex && !blockData.isEnd && (
          <>
            {!isCoach && !blockData.noArrow && (
              <GoNextBlockButton
                highlight={
                  getTaskIdFromTaskKey(taskKey) === "2-3-A" && index === 0
                }
                disabled={
                  !(
                    isCurrentIndex &&
                    isBlockUserFieldFilled(blockData, data, user)
                  )
                }
                onClick={() => {
                  complete(index);
                }}
              />
            )}
            {isCoach &&
              !isReadOnlyTask &&
              (!blockData.noBorder || blockData.removable) &&
              !blockData.hideIfPatient && (
                <CoachMenuBar
                  taskKey={taskKey}
                  thoughtRecordKey={thoughtRecordKey}
                  setData={setData}
                  index={index}
                  isThoughtRecord={isThoughtRecord}
                  hasIndentation={blockData.indentation}
                  isRemovable={blockData.removable}
                  aiCommentKey={getAiCommentKeyInBlock(blockData)}
                />
              )}
          </>
        )}
      </div>
    ) : (
      <div />
    );
  }
);

export default memo(Block);